// App
import type React from 'react';
import { useState } from 'react';
import { useSelector } from 'react-redux';

// Constellation
import { Button, Input } from '@zillow/constellation';
import { logError } from '@zg-rentals/log-error';

// Hooks
import { useEmail, useGoToAddressItem, useSuggestions } from './hooks';

// Components
import Autocomplete from '../../components/Autocomplete/index';
import type AutocompleteAddress from '../../models/AutocompleteAddress';
import NoZestimateModal from '../../components/NoZestimateModal';

// Misc / Utils
import { emitContinueAboveFoldEvent } from 'app/shared/constants/PriceMyRentalCSEvents';
import constants from '../../constants/ConstantsBundle';

import * as S from './styles';

// Types
import type { State } from '../../types/State';

const SearchModule = ({ fontColor = 'light', customTracking }: { fontColor?: string; customTracking?: () => void }) => {
  // redux hooks
  const user = useSelector((state: State) => state.user);
  const isLoggedIn = useSelector((state: State) => state.user.loggedIn);
  const recentSearches = useSelector((state: { search: Array<AutocompleteAddress> }) => state.search);

  // react hooks
  const [addressInputValue, updateAddressInputValue] = useState('');
  const [unit, updateUnit] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [validAddressItem, updateValidAddressItem] = useState<AutocompleteAddress | null>(null);

  // custom hooks
  const { email, updateEmail, validateEmail, emailError } = useEmail();
  const { addressError, isNoZestModalOpen, openNoZestModal, updateAddressError, validateAndGoToAddressItem } =
    useGoToAddressItem();
  const { suggestions, getSuggestions } = useSuggestions(recentSearches);

  // action handlers
  const handleAddressChange = (newAddressInputValue: string) => {
    updateAddressInputValue(newAddressInputValue);
    getSuggestions(newAddressInputValue);
  };
  const handleAddressSelect = (addressItem: AutocompleteAddress) => {
    // update input value when selection is made
    updateAddressInputValue(addressItem.name || '');
    // reset address input error
    updateAddressError('');
    updateValidAddressItem(addressItem);
  };

  const handleNoZestModalClose = () => {
    openNoZestModal(false);
  };

  const handleEmailKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.which === constants.ENTER_KEYCODE) {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    analyticsTracking();

    validateAndGoToAddressItem({
      addressInputValue,
      isLoggedIn,
      email,
      setIsLoading,
      unit,
      validAddressItem,
      validateEmail,
    }).catch((err: Error) => {
      logError({
        error: err,
        errorType: 'Validation',
        context: {
          details: 'Landing page: Error loading address/comps from search module',
          root: 'modules/SearchModule#handleSubmit',
        },
      });
    });
  };

  const analyticsTracking = () => {
    if (customTracking) {
      customTracking();
    } else {
      emitContinueAboveFoldEvent(user);
    }
  };

  return (
    <S.SearchModuleContainer>
      <S.AddressInputContainer>
        <Autocomplete
          items={suggestions}
          onChange={handleAddressChange}
          onSelect={handleAddressSelect}
          placeholder="Street address"
          value={addressInputValue}
        />

        <S.UnitInputContainer>
          <form data-autocomplete="off">
            <Input
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                // @ts-ignore https://github.com/desktop/desktop/issues/297
                updateUnit(e.target.value)
              }
              placeholder="Unit"
            />
          </form>
        </S.UnitInputContainer>
      </S.AddressInputContainer>

      <S.ColumnContainer>
        {!isLoggedIn && (
          <S.EmailInputContainer>
            <Input
              autoComplete="email"
              error={Boolean(emailError)}
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                // @ts-ignore https://github.com/desktop/desktop/issues/297
                updateEmail(e.target.value)
              }
              onKeyDown={handleEmailKeyDown}
              placeholder="Email address"
            />
          </S.EmailInputContainer>
        )}
        {(addressError || emailError) && <S.InputError>{addressError || emailError}</S.InputError>}
        {!isLoggedIn && (
          <S.Disclaimer fontColor={fontColor}>
            By providing your email address, you agree to receive promotional and marketing materials from Zillow.
          </S.Disclaimer>
        )}
      </S.ColumnContainer>
      <NoZestimateModal address={addressInputValue} isOpen={isNoZestModalOpen} onClose={handleNoZestModalClose} />
      <S.ButtonContainer>
        <Button buttonType="primary" onClick={handleSubmit}>
          Continue
        </Button>
        {isLoading && <S.LoadingSpinner size={'lg'} />}
      </S.ButtonContainer>
    </S.SearchModuleContainer>
  );
};

export default SearchModule;
